import { useState, useEffect } from 'react';
import { Container, Row, Col, Badge, Button, Card, Nav, Spinner, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../contexts/ApiProvider';
import { decimalToFraction } from '../../utils/mathUtils';
import { getOptimizedUrl } from '../../utils/imageUtils';
import LoadingState from '../common/LoadingState';
import RecipeFormModal from './form/RecipeFormModal';
import CookingModeModal from './CookingModeModal';

export default function RecipeDetailContainer({ recipeId }) {
    const [recipe, setRecipe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const api = useApi();
    const navigate = useNavigate();

    const [showEditModal, setShowEditModal] = useState(false);
    const [showCookingMode, setShowCookingMode] = useState(false);
    const [isCloning, setIsCloning] = useState(false); // NEW
    const [activeTab, setActiveTab] = useState('recipe');

    useEffect(() => {
        (async () => {
            setLoading(true);
            const response = await api.get(`/recipes/${recipeId}`);
            if (response.ok) {
                setRecipe(response.body);
                setError(null);
            } else {
                setError("Failed to load recipe.");
            }
            setLoading(false);
        })();
    }, [api, recipeId]);

    const handleDelete = async () => {
        if (window.confirm("Are you sure you want to delete this recipe?")) {
            const res = await api.delete(`/recipes/${recipeId}`);
            if (res.ok) {
                navigate('/recipes');
            } else {
                alert("Failed to delete recipe");
            }
        }
    };

    // --- NEW: THE CLONE HANDLER ---
    const handleClone = async () => {
        setIsCloning(true);
        const res = await api.post(`/recipes/${recipeId}/clone`);
        if (res.ok) {
            // Instantly navigate to the newly cloned recipe page!
            navigate(`/recipes/${res.body.id}`);
            // Force a reload so React completely refreshes the state with the new recipe ID
            window.location.reload();
        } else {
            alert("Failed to duplicate recipe.");
            setIsCloning(false);
        }
    };

    if (loading) return <LoadingState message="Warming up the oven..." />;
    if (error || !recipe) return <div className="text-danger text-center py-5">{error || "Recipe not found"}</div>;

    const groupItems = (items) => {
        return items.reduce((acc, item) => {
            const section = item.section_name || 'Main';
            if (!acc[section]) acc[section] = [];
            acc[section].push(item);
            return acc;
        }, {});
    };

    const ingredientGroups = groupItems(recipe.ingredients || []);
    const stepGroups = groupItems(recipe.steps || []);
    const totalTime = (recipe.prep_time || 0) + (recipe.cook_time || 0);

    const renderStars = (rating) => {
        return [...Array(5)].map((_, i) => (
            <i key={i} className={`bi fs-5 ${i < rating ? 'bi-star-fill text-warning' : 'bi-star text-muted opacity-25'}`}></i>
        ));
    };

    return (
        <Container fluid className="py-4">
            {/* Top Navigation & Actions */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <Button variant="link" className="text-decoration-none text-body p-0 d-flex align-items-center" onClick={() => navigate('/recipes')}>
                    <i className="bi bi-arrow-left me-2"></i> Back to Cookbook
                </Button>
                <div className="d-flex gap-2">
                    <Button
                        variant="primary"
                        size="sm"
                        className="rounded-pill px-4 fw-bold shadow-sm"
                        onClick={() => setShowCookingMode(true)}
                        disabled={!recipe.steps || recipe.steps.length === 0}
                    >
                        <i className="bi bi-play-fill me-1"></i> Start Cooking
                    </Button>

                    {/* --- NEW: DUPLICATE BUTTON --- */}
                    <Button variant="outline-success" size="sm" className="rounded-pill px-3 fw-bold" onClick={handleClone} disabled={isCloning}>
                        {isCloning ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-files me-1"></i> Duplicate</>}
                    </Button>

                    <Button variant="outline-primary" size="sm" className="rounded-pill px-3 fw-bold" onClick={() => setShowEditModal(true)}>
                        <i className="bi bi-pencil-fill me-1"></i> Edit
                    </Button>
                    <Button variant="outline-danger" size="sm" className="rounded-pill px-3 fw-bold" onClick={handleDelete}>
                        <i className="bi bi-trash-fill"></i>
                    </Button>
                </div>
            </div>

            {/* Hero Section */}
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4 bg-surface">
                {recipe.image_url && (
                    <div style={{ height: '300px', backgroundColor: 'var(--nook-surface-50)' }}>
                        <a href={recipe.image_url} target="_blank" rel="noopener noreferrer">
                            <img src={getOptimizedUrl(recipe.image_url, 1200)} alt={recipe.name} className="w-100 h-100" style={{ objectFit: 'cover' }} />
                        </a>
                    </div>
                )}
                <Card.Body className="p-4 p-md-5 text-center">
                    {recipe.tags && recipe.tags.length > 0 && (
                        <div className="mb-3 d-flex justify-content-center gap-2 flex-wrap">
                            {recipe.tags.map(tag => (
                                <Badge bg="primary-subtle" text="primary" key={tag.id} className="rounded-pill px-3 py-2 fw-normal">
                                    {tag.name}
                                </Badge>
                            ))}
                        </div>
                    )}

                    <h1 className="fw-bold display-6 mb-2">{recipe.name}</h1>

                    {/* --- NEW: THE VERSION SELECTOR --- */}
                    {recipe.versions && recipe.versions.length > 1 && (
                        <div className="d-flex justify-content-center mb-3">
                            <Form.Select
                                size="sm"
                                className="w-auto fw-bold text-primary bg-primary-subtle border-primary-subtle rounded-pill px-4 shadow-sm"
                                value={recipe.id}
                                onChange={(e) => {
                                    navigate(`/recipes/${e.target.value}`);
                                }}
                            >
                                {recipe.versions.map(v => (
                                    <option key={v.id} value={v.id}>
                                        {v.id === recipe.id ? `✓ Viewing: ${v.name}` : `Switch to: ${v.name}`}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    )}

                    {recipe.author && <p className="text-muted mb-4">Recipe by {recipe.author}</p>}
                    {recipe.description && <p className="lead fs-6 text-body-75 mx-auto mb-4" style={{maxWidth: '700px'}}>{recipe.description}</p>}

                    <div className="d-flex flex-wrap justify-content-center gap-4 pt-3 border-top mx-auto" style={{maxWidth: '600px'}}>
                        <div className="text-center">
                            <small className="text-header-caps text-muted d-block mb-1">Prep</small>
                            <span className="fw-bold fs-5">{recipe.prep_time || '--'} m</span>
                        </div>
                        <div className="text-center">
                            <small className="text-header-caps text-muted d-block mb-1">Cook</small>
                            <span className="fw-bold fs-5">{recipe.cook_time || '--'} m</span>
                        </div>
                        <div className="text-center">
                            <small className="text-header-caps text-muted d-block mb-1">Total</small>
                            <span className="fw-bold fs-5 text-primary">{totalTime > 0 ? `${totalTime} m` : '--'}</span>
                        </div>
                        <div className="text-center border-start ps-4">
                            <small className="text-header-caps text-muted d-block mb-1">Servings</small>
                            <span className="fw-bold fs-5">{recipe.servings || '--'}</span>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Nav variant="underline" className="custom-tabs mb-4 justify-content-center" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                <Nav.Item>
                    <Nav.Link eventKey="recipe" className="px-4 fw-bold">Recipe Details</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="trials" className="px-4 fw-bold">
                        My Trials & Tweaks
                        {recipe.trials && recipe.trials.length > 0 && (
                            <Badge bg="secondary" className="ms-2 rounded-pill">{recipe.trials.length}</Badge>
                        )}
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            {/* --- TAB CONTENT: RECIPE --- */}
            {activeTab === 'recipe' && (
                <Row className="g-5 animation-fade-in">
                    <Col lg={4}>
                        <div className="bg-surface rounded-4 p-4 shadow-sm border border-light mb-4">
                            <h4 className="fw-bold mb-4 d-flex align-items-center">
                                <i className="bi bi-basket text-primary me-2"></i> Ingredients
                            </h4>

                            {Object.keys(ingredientGroups).map((section) => (
                                <div key={section} className="mb-4">
                                    {section !== 'Main' && <h6 className="fw-bold text-header-caps text-muted border-bottom pb-2 mb-3">{section}</h6>}
                                    <ul className="list-unstyled mb-0">
                                        {ingredientGroups[section].map((ing, idx) => (
                                            <li key={idx} className="mb-3 pb-3 border-bottom border-light d-flex align-items-start flex-column">
                                                <div className="d-flex align-items-start w-100">
                                                    <i className="bi bi-circle text-primary opacity-25 me-2 mt-1" style={{fontSize: '0.6rem'}}></i>
                                                    <div className="flex-grow-1">
                                                        <span className="fw-bold me-2 text-dark">
                                                            {ing.quantity ? decimalToFraction(ing.quantity) : ''} {ing.unit === 'Other' ? ing.custom_unit : ing.unit}
                                                        </span>
                                                        <span className="text-body-75">{ing.name}</span>
                                                        {ing.notes && <span className="text-muted small ms-1 fst-italic">({ing.notes})</span>}
                                                    </div>
                                                </div>

                                                {ing.alternatives && ing.alternatives.length > 0 && (
                                                    <div className="mt-2 ms-3 ps-3 border-start border-primary border-opacity-25 w-100">
                                                        <div className="small text-primary fw-bold text-header-caps mb-1" style={{fontSize: '0.65rem'}}>Approved Swaps:</div>
                                                        {ing.alternatives.map((alt, aIdx) => (
                                                            <div key={aIdx} className="small text-muted mb-1">
                                                                <span className="fw-medium text-body-75">{alt.quantity ? decimalToFraction(alt.quantity) : ''} {alt.unit === 'Other' ? alt.custom_unit : alt.unit}</span> {alt.name}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>

                        {recipe.equipment && recipe.equipment.length > 0 && (
                            <div className="bg-surface rounded-4 p-4 shadow-sm border border-light">
                                <h5 className="fw-bold mb-3 text-header-caps">Equipment</h5>
                                <ul className="list-unstyled mb-0 d-flex flex-wrap gap-2">
                                    {recipe.equipment.map((eq, idx) => (
                                        <li key={idx}><Badge bg="light" text="dark" className="border fw-normal px-2 py-1">{eq.name}</Badge></li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </Col>

                    <Col lg={8}>
                        <div className="bg-surface rounded-4 p-4 p-md-5 shadow-sm border border-light h-100">
                            <h4 className="fw-bold mb-4 d-flex align-items-center">
                                <i className="bi bi-list-check text-primary me-2"></i> Instructions
                            </h4>

                            {Object.keys(stepGroups).map((section) => (
                                <div key={section} className="mb-5">
                                    {section !== 'Main' && <h5 className="fw-bold text-header-caps text-primary border-bottom pb-2 mb-4">{section}</h5>}

                                    {stepGroups[section].map((step, idx) => (
                                        <div key={idx} className="d-flex mb-4 pb-4 border-bottom border-light">
                                            <div className="me-3 me-md-4 pt-1">
                                                <div className="bg-primary text-white fw-bold rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{width: '32px', height: '32px'}}>
                                                    {step.order_index}
                                                </div>
                                            </div>
                                            <div className="flex-grow-1">
                                                <p className="fs-6 text-body-75 mb-3 lh-lg" style={{whiteSpace: 'pre-wrap'}}>
                                                    {step.instruction}
                                                </p>
                                                {step.image_url && (
                                                    <a href={step.image_url} target="_blank" rel="noopener noreferrer" title="Click to view full resolution">
                                                        <img src={getOptimizedUrl(step.image_url, 600)} alt={`Step ${step.order_index}`} className="img-fluid rounded-3 shadow-sm mt-2 hover-opacity-75 transition-all" style={{maxHeight: '250px', cursor: 'zoom-in'}} />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </Col>
                </Row>
            )}

            {/* --- TAB CONTENT: TRIALS & TWEAKS --- */}
            {activeTab === 'trials' && (
                <div className="animation-fade-in mx-auto" style={{ maxWidth: '800px' }}>
                    {(!recipe.trials || recipe.trials.length === 0) ? (
                        <div className="text-center py-5 bg-surface rounded-4 shadow-sm border border-light">
                            <div className="d-inline-flex align-items-center justify-content-center bg-light text-muted rounded-circle mb-3" style={{width: '64px', height: '64px'}}>
                                <i className="bi bi-journal-x fs-2"></i>
                            </div>
                            <h5 className="fw-bold">No Trials Yet</h5>
                            <p className="text-muted mb-4">Click 'Start Cooking' to run this recipe. When you finish, you can log your results and tweaks here!</p>
                        </div>
                    ) : (
                        <div className="timeline-container ps-2 ms-2 border-start border-2 border-primary border-opacity-25">
                            {recipe.trials.map(trial => (
                                <Card key={trial.id} className="border-0 shadow-sm rounded-4 mb-4 ms-3 position-relative bg-surface">
                                    <div className="position-absolute bg-primary rounded-circle" style={{ width: '12px', height: '12px', left: '-23px', top: '24px' }}></div>
                                    <Card.Body className="p-4">
                                        <div className="d-flex justify-content-between align-items-start mb-3 border-bottom pb-3">
                                            <div>
                                                <div className="small text-muted fw-bold text-header-caps mb-1">
                                                    {new Date(trial.date).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })}
                                                </div>
                                                <div className="d-flex gap-1">
                                                    {renderStars(trial.rating)}
                                                </div>
                                            </div>
                                            <Badge bg={trial.rating >= 4 ? 'success' : (trial.rating >= 3 ? 'warning' : 'danger')} className="rounded-pill text-header-caps">
                                                {trial.rating >= 4 ? 'Success' : (trial.rating >= 3 ? 'Okay' : 'Needs Work')}
                                            </Badge>
                                        </div>

                                        {trial.tweaks && (
                                            <div className="bg-primary-subtle border border-primary-subtle rounded-3 p-3 mb-3">
                                                <div className="small fw-bold text-primary text-header-caps mb-1"><i className="bi bi-wrench-adjustable me-1"></i> Tweaks & Swaps</div>
                                                <div className="text-primary-emphasis" style={{whiteSpace: 'pre-wrap'}}>{trial.tweaks}</div>
                                            </div>
                                        )}

                                        {trial.notes && (
                                            <div className="mb-3">
                                                <div className="small fw-bold text-muted text-header-caps mb-1">Notes</div>
                                                <p className="text-body-75 mb-0" style={{whiteSpace: 'pre-wrap'}}>{trial.notes}</p>
                                            </div>
                                        )}

                                        {trial.images && trial.images.length > 0 && (
                                            <div className="mt-3">
                                                {trial.images.map((imgUrl, idx) => (
                                                    <a key={idx} href={imgUrl} target="_blank" rel="noopener noreferrer" title="Click to view full resolution">
                                                        <img src={getOptimizedUrl(imgUrl, 400)} alt="Trial result" className="img-fluid rounded-3 me-2 shadow-sm border hover-opacity-75 transition-all" style={{ maxHeight: '150px', objectFit: 'cover', cursor: 'zoom-in' }} />
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Modals */}
            <RecipeFormModal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                recipe={recipe}
                onSuccess={(updatedRecipe) => {
                    setRecipe(updatedRecipe);
                    setShowEditModal(false);
                }}
            />

            <CookingModeModal
                show={showCookingMode}
                onHide={() => setShowCookingMode(false)}
                recipe={recipe}
            />
        </Container>
    );
}
